import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, TableRow, TableCell, Typography, useTheme } from '@mui/material';
import { Link as LinkIcon, Campaign } from '@mui/icons-material';
import {
  GlassCard, GlassTable, ShimmerSkeleton, EmptyState,
} from '../../../components/admin/ui';
import { getAcquisition } from '../../../analytics/adminApi';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPct(v) {
  return v == null ? '—' : `${(v * 100).toFixed(1)}%`;
}

function fmtRevenue(paise) {
  if (!paise) return '₹0';
  return `₹${(paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AnalyticsAcquisition({ filters }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState(true);
  const [referrers, setReferrers] = useState([]);
  const [utm, setUtm] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getAcquisition({
      from: filters.from,
      to: filters.to,
      includeInternalRoles: filters.includeInternalRoles,
      includeBots: filters.includeBots,
    }).then((d) => {
      if (!cancelled) {
        setReferrers(d.referrers || []);
        setUtm(d.utm || []);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [filters.from, filters.to, filters.includeInternalRoles, filters.includeBots]);

  return (
    <Box>
      <Grid container spacing={2}>
        {/* ── Referrers ──────────────────────────────────────────────────── */}
        <Grid item xs={12} md={5}>
          <GlassCard sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LinkIcon sx={{ fontSize: 18, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }} />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: isDark ? '#F8FAFC' : '#0F172A' }}
              >
                Referrers
              </Typography>
            </Box>
            {loading ? (
              <ShimmerSkeleton height={320} />
            ) : referrers.length === 0 ? (
              <EmptyState
                title="No referrer data"
                description="Referrer data will appear once the backend is live."
                iconIndex={0}
              />
            ) : (
              <GlassTable
                columns={[
                  { label: 'Host' },
                  { label: 'Sessions', align: 'right' },
                  { label: 'Conv. Rate', align: 'right' },
                ]}
                sx={{ maxHeight: 380 }}
              >
                {referrers.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell
                      sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {row.host || '(direct)'}
                    </TableCell>
                    <TableCell align="right">{(row.sessions || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{fmtPct(row.convRate)}</TableCell>
                  </TableRow>
                ))}
              </GlassTable>
            )}
          </GlassCard>
        </Grid>

        {/* ── UTM ────────────────────────────────────────────────────────── */}
        <Grid item xs={12} md={7}>
          <GlassCard sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Campaign sx={{ fontSize: 18, color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)' }} />
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ color: isDark ? '#F8FAFC' : '#0F172A' }}
              >
                UTM Campaigns
              </Typography>
            </Box>
            {loading ? (
              <ShimmerSkeleton height={320} />
            ) : utm.length === 0 ? (
              <EmptyState
                title="No UTM data"
                description="UTM tracking will appear once campaign links are used."
                iconIndex={1}
              />
            ) : (
              <GlassTable
                columns={[
                  { label: 'Source' },
                  { label: 'Medium' },
                  { label: 'Campaign' },
                  { label: 'Sessions', align: 'right' },
                  { label: 'Conversions', align: 'right' },
                  { label: 'Revenue', align: 'right' },
                ]}
                sx={{ maxHeight: 380 }}
              >
                {utm.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.source || '—'}</TableCell>
                    <TableCell>{row.medium || '—'}</TableCell>
                    <TableCell
                      sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {row.campaign || '—'}
                    </TableCell>
                    <TableCell align="right">{(row.sessions || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(row.conversions || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{fmtRevenue(row.revenuePaise)}</TableCell>
                  </TableRow>
                ))}
              </GlassTable>
            )}
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
}

AnalyticsAcquisition.propTypes = {
  filters: PropTypes.object.isRequired,
};
